/* eslint-disable no-nested-ternary */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Textarea from "components/atoms/Textarea/Textarea";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import FormField from "components/molecules/FormField/FormField";
import { IFieldProps } from "types/Organisational";
import { filterWhenNonSelected } from "helpers/commonHelpers";
import getLabel from "helpers/getLabel";
import { useFormContext, useWatch } from "react-hook-form";
import { Dispatch, useEffect, useState } from "react";
import { getAirports, getSeaports } from "api";
import SearchSelect from "../Common/SearchSelect";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "../../RequestFormState";

type OrganisationFormFieldProps = Omit<IFieldProps, "minimum_characters" | "maximum_characters"> & {
  isQuoteRequest: boolean;
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  isConsolidated?: boolean;
};

function OrganisationFormField({
  additional_information,
  label,
  field_id,
  type,
  dropdown_values,
  mandatory,
  placeholder,
  initial_data,
  state,
  isQuoteRequest,
  dispatch,
  isConsolidated,
}: OrganisationFormFieldProps) {
  const [triggeredInitialDisabled, setTriggeredInitialDisabled] = useState(false);
  const { getValues, setValue, register, watch } = useFormContext();

  const [costRecoveryRequired] = useWatch({
    name: ["organizational.cost_recovery_required"],
  });

  const costRecoveryFieldIds = ["custom_cost_recovery_field1", "custom_cost_recovery_field2"];

  // Check if this field has any conditional updates, we will use this value to determine if we use the handleConditionalSelect function on the onChange event
  let hasConditionalUpdate = false;
  if (dropdown_values) hasConditionalUpdate = dropdown_values.some((item: any) => item.updates.length > 0);

  /**
   * This function is used to handle the conditional updates for the dropdown fields
   * It will update the value of any fields and disable any fields that are specified in the updates array
   * @param e - The event object
   * @param previousValue - The previous value of the field
   */
  const handleConditionalSelect = (e: any, previousValue: string) => {
    const fieldsToUpdate = e?.[0]?.updates;

    const previousValueObject = dropdown_values.find((item: any) => item.value === previousValue);
    const previousFieldsToUpdate = previousValueObject?.updates;

    // If the previous value had updates, we need to revert their disabled status
    if (previousFieldsToUpdate) {
      previousFieldsToUpdate.forEach((fieldToUpdate: any) => {
        const { selector } = fieldToUpdate;
        dispatch({
          type: RequestFormActionType.UPDATE_DISABLED_FIELDS,
          payload: {
            key: selector,
            value: false,
          },
        });
      });
    }

    // Update the value and disabled state of the fields
    fieldsToUpdate.forEach((fieldToUpdate: any) => {
      const { selector, value, disable } = fieldToUpdate;

      // If the selector is a seaport or airport code, we need to update the address type field to match
      if (selector.includes("seaport_code") || selector.includes("airport_code")) {
        const selectorPrefix = selector.split("_")[0];
        const selectorSuffix = selector.split("_")[1];
        const addressTypeFieldToUpdate = `${selectorPrefix}_address_type`;

        setValue(addressTypeFieldToUpdate, selectorSuffix);

        const getPorts = selector.includes("seaport_code") ? getSeaports : getAirports;
        getPorts(value).then((res: any) => {
          const { results } = res.data;
          const portCodeKey = selector.includes("seaport_code") ? "un_locode" : "code";
          const { name } = results.find((item: any) => item[portCodeKey] === value);
          setValue(`${selectorPrefix}_${selectorSuffix}`, name);
        });
      }

      // Update the value of the field
      setValue(selector, value);

      // Disable the field if necessary
      if (disable) {
        dispatch({
          type: RequestFormActionType.UPDATE_DISABLED_FIELDS,
          payload: {
            key: selector,
            value: true,
          },
        });
      }
    });
  };

  /**
   * Get the fieldId to be used for the input field
   * @param fieldId - The fieldId used to determine the final fieldId
   * @returns the fieldId to be used for the input field
   */
  const getInputFieldId = (fieldId: string) => {
    if (fieldId === "shipment_title") return isQuoteRequest ? "shipment.title" : "quick_request.title";
    return `organizational.${fieldId}`;
  };

  /**
   * Get the reason code dropdown values
   * @param dropdownValues - The dropdown values to be filtered
   * @returns the filtered dropdown values
   */
  const getReasonCodeDropdownValues = (dropdownValues: Array<any>) => {
    if (!state.locationDetails?.organizational_options?.reason_code_group?.dropdown_values?.length) return dropdownValues;
    if (!getValues("organizational.reason_code_group") || !dropdownValues?.length) return [];
    return dropdownValues.filter((item) => item.group_name === getValues("organizational.reason_code_group"));
  };

  /**
   * Filter the dropdown values based on the fieldId
   * @param fieldId - The fieldId used to determine the final dropdown values
   * @param orignalDropdownValues - The dropdown values to be filtered
   * @returns the filtered dropdown values
   */
  const filterDropdownValues = (fieldId: string, orignalDropdownValues: Array<any>) => {
    if (fieldId === "reason_code") return getReasonCodeDropdownValues(orignalDropdownValues);
    if (fieldId === "cost_center") {
      const adjustedDropdownValues = orignalDropdownValues.map((item) => ({
        ...item,
        key: item.key.replace(/\n/g, ""),
        label: item.label.replace(/\n/g, ""),
      }));
      return adjustedDropdownValues;
    }
    return orignalDropdownValues;
  };

  const dropdownOptions = type === "dropdown" ? filterDropdownValues(field_id, dropdown_values) : [];

  const currentValue = watch(`organizational.${field_id}`);
  // Disable all the fields that were linked in the current selected dropdown option
  // Because sometimes `dropdown_values` has an attribute `updates`.
  useEffect(() => {
    if (type !== "dropdown" || triggeredInitialDisabled || dropdownOptions.length === 0 || !currentValue) return;
    const selectedDropdownOption = dropdownOptions.find((option) => option.value === currentValue);
    const fieldsToUpdate = selectedDropdownOption?.updates;
    if (!fieldsToUpdate || fieldsToUpdate?.length === 0) return;
    fieldsToUpdate.forEach((fieldToUpdate: any) => {
      if (fieldToUpdate.disable) {
        dispatch({
          type: RequestFormActionType.UPDATE_DISABLED_FIELDS,
          payload: {
            key: fieldToUpdate.selector,
            value: true,
          },
        });
      }
    });
    setTriggeredInitialDisabled(true);
  }, [dropdown_values, currentValue]);

  // Don't render the cost recovery fields if cost recovery is False
  // But, render a hidden input field instead so that the property is still submitted and backend validation can be done
  if (costRecoveryFieldIds.includes(field_id) && costRecoveryRequired !== "True")
    return <input type="hidden" {...register(`organizational.${field_id}`, { required: false })} />;

  // Render the form field based on the type
  switch (type) {
    case "textarea":
      return (
        <div className="d-flex flex-column" key={field_id}>
          <div className={`pb-2 text-secondary ${mandatory ? "required" : ""}`}>
            <span>{label}</span>
            {additional_information && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="">
                    <span className="font-weight-normal">{additional_information}</span>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="font-12 text-primary ml-2" />
              </OverlayTrigger>
            )}
          </div>
          <Textarea
            name={`organizational.${field_id}`}
            placeholder={`Add ${label.toLowerCase()}`}
            rows={2}
            className="text-body bg-white"
            defaultValue={initial_data}
            validations={{ shouldUnregister: true }}
          />
        </div>
      );
    case "textinput":
      return (
        <div className="input-element w-192 mr-8 mt-3" key={field_id}>
          <FormField
            additional_information={additional_information}
            label={label}
            mandatory={field_id === "cost_recovery_required" ? true : mandatory}
            type={type}
            dropdown_values={dropdown_values || []}
            field_id={getInputFieldId(field_id)}
            placeholder={placeholder}
            disabled={state.disabledFields[getInputFieldId(field_id)]}
            validations={{ shouldUnregister: true }}
          />
        </div>
      );
    case "dropdown":
      return (
        <div className={`w-192 mr-8 ${isConsolidated ? "organization-dropdown" : "mt-3"} `} key={field_id}>
          <SearchSelect
            options={dropdownOptions}
            name={`organizational.${field_id}`}
            required={mandatory}
            {...(field_id === "cost_recovery_required" && {
              defaultValue: dropdown_values?.find((item: any) => item.key === "Yes")?.value || "True",
            })}
            filterBy={filterWhenNonSelected}
            disable={state.disabledFields[`organizational.${field_id}`]}
            label={
              <>
                {getLabel(label, "uppercase")}
                <span>
                  {additional_information && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="">
                          <span className="font-weight-normal">{additional_information}</span>
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="font-12 text-primary ml-2" />
                    </OverlayTrigger>
                  )}
                </span>
              </>
            }
            placeholder={placeholder}
            // If the field has conditional updates or if the field is the reason code group field, we need to handle the onChange event
            {...((hasConditionalUpdate || field_id === "reason_code_group") && {
              onSelect: (e: any) => {
                // Clear the reason code field if the reason code group is changed
                if (field_id === "reason_code_group") setValue("organizational.reason_code", "");
                // Handle the conditional updates if necessary
                if (hasConditionalUpdate) {
                  handleConditionalSelect(e, getValues(`organizational.${field_id}`));
                }
              },
            })}
            shouldUnregister
          />
        </div>
      );
    default:
      return (
        <div className="input-element w-192" key={field_id}>
          <FormField
            type="text"
            field_id={`organizational.${field_id}`}
            label={label}
            defaultValue={dropdown_values?.[0]?.value}
            mandatory
            disabled
            validations={{ shouldUnregister: true }}
          />
        </div>
      );
  }
}

export default OrganisationFormField;

OrganisationFormField.defaultProps = {
  isConsolidated: false,
};
